import React from 'react';
import { Button, Modal, Loader } from '@getro/rombo';
import { ExternalLink, Info } from 'lucide-react';
import { Box, Flex, Link, Text } from 'rebass/styled-components';
import pluralize from 'pluralize';
import { NavLink, Switch, Route } from 'react-router-dom';

import { MainLayoutHeader } from '../../components/atoms/mainLayoutHeader';
import MainLayout from '../../components/layout/Main';
import usePageHook from '../../hooks/usePageHook';
import { Toolbar } from '../../components/molecules/agGrid/components/toolbar';
import { Hero } from './hero';
import { HowItWorks } from './howItWorks';
import { HiresTableView } from './hiresTableView';
import { HiresSidebar } from './hiresFilters/hiresSidebar';
import { ContactDetail } from '../../components/organisms/contactDetail';
import { useHiresPage } from './hooks/useHiresPage';
import { HiresNoResults } from './hiresNoResults';
import { HiresMainSkeleton } from './hiresMainSkeleton';

export const Hires = () => {
  usePageHook('Hires');

  const {
    // Data
    data,
    isLoading,
    isFetching,
    // Filters
    filters,
    setFilters,
    keyword,
    handleKeywordChangeWrapper,
    filtersCount,
    urlQueryParams,
    // UI State
    showFilters,
    setShowFilters,
    columns,
    setColumns,
    showHowItWorksModal,
    setShowHowItWorksModal,
    // Loading states
    showMainSkeleton,
    showRenderSkeleton,
    hasActiveFilters,
    // Pagination
    hasMore,
    onLoadMore,
    isLoadingMore,
    // Table
    tableRef,
    initialSortBy,
    currentSort,
    hiresSortOptions,
    // Handlers
    toolbarOptionChange,
    onSort,
    // Other
    activeNetwork,
    defaultColumns,
    isDownloadingCsv,
    history,
    page,
  } = useHiresPage();

  return (
    <MainLayout containerProps={{ pb: 0 }}>
      <MainLayoutHeader title="Hires" />
      <Flex mx="auto" width="100%" flexDirection="column" sx={{ height: 'calc(100vh - 56px)', overflow: 'hidden' }}>
        <Box px="16px" flex="0 0 auto">
          <Flex
            mt="8px"
            pb="24px"
            justifyContent="space-between"
            alignItems={['flex-start', 'center']}
            flexDirection={['column', 'row']}
          >
            <Text fontSize="14px" color="text.main">
              {`Celebrate your wins! Track when one of your `}
              <Link
                as={NavLink}
                to={`/networks/${activeNetwork.slug}/contacts`}
                sx={{ textDecoration: 'none', color: 'text.main', fontWeight: '600' }}
              >
                {data?.meta ? `${data.meta.contactsTotal} ${pluralize('contact', data.meta.contactsTotal)}` : '-'}
              </Link>
              {` joins one of your `}
              <Link
                as={NavLink}
                to={`/networks/${activeNetwork.slug}/companies`}
                sx={{ textDecoration: 'none', color: 'text.main', fontWeight: '600' }}
              >
                {data?.meta ? `${data.meta.companiesTotal} ${pluralize('company', data.meta.companiesTotal)}` : '-'}
              </Link>
              .
            </Text>
            <Flex mt={['16px', 0]} flex="0 0 auto">
              <Flex
                ml={[0, '16px']}
                alignItems="center"
                color="text.subtle"
                sx={{ whiteSpace: 'nowrap', cursor: 'pointer' }}
                onClick={() => setShowHowItWorksModal(true)}
              >
                <Box as={Info} width="16px" height="16px" aria-hidden="true" flex="0 0 auto" />
                <Text ml="8px" fontSize="14px">
                  How it works
                </Text>
              </Flex>
              <Flex
                as={Link}
                ml="16px"
                alignItems="center"
                fontSize="14px"
                color="text.subtle"
                href="mailto:product@getro.com?subject=Report on hires feedback"
                sx={{ whiteSpace: 'nowrap', textDecoration: 'none' }}
              >
                <Box as={ExternalLink} mr="8px" width="16px" height="16px" aria-hidden="true" flex="0 0 auto" />
                Share feedback
              </Flex>
            </Flex>
          </Flex>
          <Hero network={activeNetwork} meta={data?.meta} isLoading={isLoading} />
        </Box>
        <Box mt="16px" flex="1" sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {showMainSkeleton && <HiresMainSkeleton />}
          {!showMainSkeleton && !showRenderSkeleton && data?.meta?.hiresTotal === 0 && !hasActiveFilters && (
            <Box px={[0, '40px']} py="24px">
              <HowItWorks />
            </Box>
          )}
          {!showMainSkeleton && (data?.meta?.hiresTotal > 0 || hasActiveFilters || showRenderSkeleton) && (
            <>
              <Toolbar
                view="table"
                toolbarOptionChange={toolbarOptionChange}
                selectedColumns={columns || defaultColumns}
                isDownloadingCsv={isDownloadingCsv}
                type="default"
                page="hires"
                filtersVisible={showFilters}
                filtersCount={filtersCount}
                tableRef={tableRef}
                currentSort={currentSort}
                sortOptions={hiresSortOptions}
                showLayout={false}
              />
              <Flex flex="1" sx={{ position: 'relative', bg: 'neutral.0', overflow: 'hidden' }}>
                <HiresSidebar
                  toggleFilters={() => setShowFilters(!showFilters)}
                  filters={filters}
                  setFilters={setFilters}
                  activeNetwork={activeNetwork}
                  keyword={keyword}
                  onKeywordChange={handleKeywordChangeWrapper}
                  filtersCount={filtersCount}
                  showFilters={showFilters}
                />
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    minWidth: ['100vw', '100vw', 0],
                    transition: 'margin-left 0.3s ease-in-out',
                    marginLeft: showFilters ? '0px' : '-268px',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {(data?.items?.length > 0 || showRenderSkeleton) && (
                    <Box flex="1" sx={{ overflow: 'hidden' }}>
                      <HiresTableView
                        ref={tableRef}
                        data={data?.items || []}
                        activeNetwork={activeNetwork}
                        columns={columns || defaultColumns}
                        setColumns={setColumns}
                        onLoadMore={onLoadMore}
                        hasMore={hasMore}
                        isLoadingMore={isLoadingMore}
                        onSort={onSort}
                        initialSortBy={initialSortBy}
                        showSkeleton={showRenderSkeleton}
                      />
                    </Box>
                  )}
                  {!showRenderSkeleton && !isFetching && data?.items?.length === 0 && hasActiveFilters && (
                    <HiresNoResults />
                  )}
                </Box>
              </Flex>
            </>
          )}
          {!showMainSkeleton && !showRenderSkeleton && isFetching && page > 1 && (
            <Flex height="60px" py="16px" flexDirection="column" alignItems="center" maxWidth="480px" mx="auto">
              <Box>
                <Loader />
              </Box>
            </Flex>
          )}
        </Box>
        {showHowItWorksModal && (
          <Modal
            variant="xLarge"
            isOpen
            actions={
              <Flex justifyContent={['flex-end']}>
                <Button onClick={() => setShowHowItWorksModal(false)}>Got it</Button>
              </Flex>
            }
            onCancel={() => setShowHowItWorksModal(false)}
          >
            <Box px="16px" py="24px">
              <HowItWorks />
            </Box>
          </Modal>
        )}
        <Switch>
          <Route path={`/networks/${activeNetwork.slug}/hires/:contactId`}>
            <ContactDetail
              queryParams={urlQueryParams}
              isTalentNetworkEnabled={!activeNetwork?.tnHidden}
              onClose={() => {
                history.push(`/networks/${activeNetwork.slug}/hires${history.location.search}`);
              }}
              page="hires"
              showMoreOptions
            />
          </Route>
        </Switch>
      </Flex>
    </MainLayout>
  );
};
